import {
  Box,
  FormControlLabel,
  IconButton,
  styled,
  Typography,
} from '@mui/material';

import { getDropdownOffset } from './getDropdownOffset';

import type { DropdownOffset } from '../types';
import type { EDropdownPlacement } from './dropdownPlacement.enum';

export const baseDropdownClasses = {
  root: `Dropdown-root`,
  section: `Dropdown-section`,
  divider: `Dropdown-divider`,
  iconRow: `Dropdown-iconRow`,
  label: `Dropdown-label`,
  button: `Dropdown-button`,
  icon: `Dropdown-icon`,
  triggerRoot: `DropdownTrigger-root`,
};

type TriggerProps = {
  placement?: EDropdownPlacement;
  offset?: DropdownOffset;
};
const excludedProps: PropertyKey[] = ['absolute'];

// Trigger wrapper
export const BaseDropdownTrigger = styled(IconButton, {
  shouldForwardProp: p => !excludedProps.includes(p),
})<TriggerProps>(({ theme, placement = 'inline', offset }) => {
  const o = getDropdownOffset(offset);
  return {
    backgroundColor: theme.palette.mapBlue[700],
    border: `2px solid ${theme.palette.mapBlue[600]}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[3],
    color: theme.palette.common.white,
    width: theme.spacing(5),
    height: theme.spacing(5),
    overflow: 'hidden',

    '& svg': {
      fill: theme.palette.common.white,
    },

    '&:hover': {
      backgroundColor: theme.palette.mapBlue[800],
    },

    '&.Mui-disabled': {
      opacity: 0.3,
    },

    [theme.breakpoints.down('lg')]: {
      width: theme.spacing(4),
      height: theme.spacing(4),
    },

    ...(placement === 'fixed' && {
      position: 'fixed',
      top: o.top,
      right: o.right,
      zIndex: 100,
    }),
    ...(placement === 'absolute' && {
      position: 'absolute',
      top: o.top,
      right: o.right,
      zIndex: 100,
    }),
  };
});

export const BaseDropdownLabel = styled(FormControlLabel)(
  ({ theme: { spacing, utils } }) => ({
    alignItems: 'center',
    gap: spacing(1),
    marginBottom: spacing(1),

    minHeight: 'unset', // prevent MUI default minHeight
    height: utils.clamp(20, 32),

    [`& .MuiFormControlLabel-label`]: {
      fontSize: utils.clamp(12, 16),
      lineHeight: 1.2,
    },
  }),
);

// Dropdown content
export const BaseDropdown = styled(Box)(
  ({ theme: { spacing, palette, utils, breakpoints } }) => ({
    [`&.${baseDropdownClasses.root}`]: {
      padding: `${spacing(1)} 0`,
      backgroundColor: palette.mapBlue[700],
      borderRadius: spacing(1.5),
      color: palette.common.white,
      width: 260,

      [breakpoints.down('xl')]: {
        width: 220,
      },
    },

    [`& .${baseDropdownClasses.section}`]: {
      padding: `0 ${spacing(1)}`,
      '&:last-of-type': {
        marginBottom: 0,
      },
    },

    [`& .${baseDropdownClasses.button}`]: {
      backgroundColor: palette.mapBlue[700],
      color: palette.common.white,

      '& svg': {
        fill: palette.common.white,
      },

      '&:hover': {
        backgroundColor: palette.mapBlue[800],
      },

      '&.Mui-disabled': {
        opacity: 0.3,
        color: palette.common.white,
      },
    },

    [`& .${baseDropdownClasses.divider}`]: {
      width: '100%',
      backgroundColor: palette.mapBlue[500],
      margin: `${spacing(1)} 0`,
    },

    [`& .${baseDropdownClasses.iconRow}`]: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1),
      cursor: 'pointer',
      fontSize: utils.clamp(12, 16),
    },

    [`& .${baseDropdownClasses.label}`]: {
      color: palette.common.white,
      '&.Mui-checked': {
        color: palette.common.white,
      },
    },

    [`& .${baseDropdownClasses.icon}`]: {
      color: palette.common.white,
      fill: palette.common.white,
      display: 'block',
      width: spacing(3),
      height: spacing(3),

      [breakpoints.down('xl')]: {
        width: spacing(2.5),
        height: spacing(2.5),
      },
    },
  }),
);

// Shared typography for dropdown
export const DropdownTypography = styled(Typography)(
  ({ theme: { utils } }) => ({
    fontSize: utils.clamp(12, 16),
  }),
);
